import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import {
  IMAGE_CROP_PRESETS,
  type CropPresetId,
} from '../../constants/imageCropPresets';
import {
  exportCroppedImage,
  exportFocalOnly,
  initialCoverScale,
  clampCropTransform,
  type CropTransform,
} from '../../utils/imageCropUtils';
import { prepareImageFileForUpload } from '../../utils/prepareImageFileForUpload';

type ImageCropModalProps = {
  isOpen: boolean;
  onClose: () => void;
  file: File | null;
  presetId: CropPresetId;
  onConfirm: (result: {
    file: File;
    focal: { x: number; y: number; zoom?: number };
    cropped: boolean;
  }) => void | Promise<void>;
  busy?: boolean;
  closeOnConfirm?: boolean;
};

const FRAME_SIZE = 280;

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  isOpen,
  onClose,
  file,
  presetId,
  onConfirm,
  busy = false,
  closeOnConfirm = true,
}) => {
  const preset = IMAGE_CROP_PRESETS[presetId];
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const resize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  const frameW = Math.min(Math.round(FRAME_SIZE * preset.aspect), Math.max(120, viewportWidth - 96), 448);
  const frameH = frameW / preset.aspect;

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imgSize, setImgSize] = useState({ w: 1, h: 1 });
  const [transform, setTransform] = useState<CropTransform>({ scale: 1, offsetX: 0, offsetY: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!file || !isOpen) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    const img = new Image();
    img.onload = () => {
      const cover = initialCoverScale(img.width, img.height, frameW, frameH);
      setImgSize({ w: img.width, h: img.height });
      setTransform({ scale: cover, offsetX: 0, offsetY: 0 });
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file, isOpen, frameW, frameH]);

  const maskClass = useMemo(() => {
    if (preset.shape === 'circle') return 'rounded-full';
    if (preset.shape === 'square') return 'rounded-lg';
    return 'rounded-xl';
  }, [preset.shape]);

  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      ox: transform.offsetX,
      oy: transform.offsetY,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging || !dragStart.current) return;
    setTransform((t) => clampCropTransform({
      ...t,
      offsetX: dragStart.current!.ox + (e.clientX - dragStart.current!.x),
      offsetY: dragStart.current!.oy + (e.clientY - dragStart.current!.y),
    }, imgSize.w, imgSize.h, frameW, frameH));
  };

  const onPointerUp = () => {
    setDragging(false);
    dragStart.current = null;
  };

  const handleConfirm = useCallback(async () => {
    if (!file) return;
    setProcessing(true);
    setError(null);
    try {
      if (preset.mode === 'focal') {
        const { focal } = await exportFocalOnly(file, preset, transform, frameH);
        // Focal uploads still send the full image — compress so iOS originals stay under the cap.
        const prepared = await prepareImageFileForUpload(file, { maxLongEdge: 2048 });
        await onConfirm({ file: prepared, focal, cropped: false });
      } else {
        const { blob, focal } = await exportCroppedImage(file, preset, transform, frameH);
        const out = new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
        await onConfirm({ file: out, focal, cropped: true });
      }
      if (closeOnConfirm) onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save photo. Please try again.');
    } finally {
      setProcessing(false);
    }
  }, [file, onConfirm, onClose, preset, transform, frameH, closeOnConfirm]);

  const displayedW = imgSize.w * transform.scale;
  const displayedH = imgSize.h * transform.scale;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={preset.mode === 'focal' ? 'Set photo focus' : 'Crop photo'}
      size="md"
    >
      <div className="space-y-5">
        {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
        {preset.hint ? (
          <p className="text-sm text-gray-400 leading-relaxed">{preset.hint}</p>
        ) : null}

        <div className="flex justify-center">
          <div
            className={`relative overflow-hidden border border-white/[0.10] bg-[#0a0a0a] shadow-inner ${maskClass}`}
            style={{ width: frameW, height: frameH, touchAction: 'none', flexShrink: 0 }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
          >
            {previewUrl ? (
              <img
                src={previewUrl}
                alt=""
                draggable={false}
                className="absolute select-none pointer-events-none max-w-none"
                style={{
                  width: displayedW,
                  height: displayedH,
                  left: frameW / 2 + transform.offsetX - displayedW / 2,
                  top: frameH / 2 + transform.offsetY - displayedH / 2,
                }}
              />
            ) : null}
            {preset.shape === 'circle' ? (
              <div
                className="pointer-events-none absolute inset-0 ring-2 ring-orange-500/40 ring-inset rounded-full"
                aria-hidden
              />
            ) : null}
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-400 mb-2 block">
            Zoom · {(transform.scale / initialCoverScale(imgSize.w, imgSize.h, frameW, frameH)).toFixed(2)}×
          </label>
          <input
            type="range"
            min={initialCoverScale(imgSize.w, imgSize.h, frameW, frameH)}
            max={initialCoverScale(imgSize.w, imgSize.h, frameW, frameH) * 3}
            step={0.01}
            value={transform.scale}
            onChange={(e) =>
              setTransform((t) => clampCropTransform({ ...t, scale: parseFloat(e.target.value) }, imgSize.w, imgSize.h, frameW, frameH))
            }
            className="w-full accent-orange-500"
          />
        </div>

        <p className="text-xs text-gray-500">
          Preview: {preset.label}
          {preset.mode === 'focal' ? ' · full photo is kept' : ' · saved crop matches this frame'}
        </p>

        <div className="flex gap-3 justify-end pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={processing || busy}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void handleConfirm()}
            disabled={!file || processing || busy}
          >
            {processing || busy ? 'Processing…' : preset.mode === 'focal' ? 'Save focus' : 'Save crop'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ImageCropModal;
